import { Box } from "@chakra-ui/react";
import React from "react";
import Balancer from "react-wrap-balancer";
import {
  AlertIcon,
  CalendarIcon,
  ClockIcon,
  CoffeeIcon,
  CornerDownRightIcon,
  GiftIcon,
  GlobeIcon,
  HeartIcon,
  LightningIcon,
  MinusCircleIcon,
  MoneyIcon,
  PeopleIcon,
  PersonWithCheckIcon,
  PinIcon,
  RefreshIcon,
  SettingsIcon,
  SparklesIcon,
  TrendIcon,
} from "ui/icons";
import hero from "../../../../public/assets/cdp-industries/heros/qsr.png";
import { dataBreachStat, personalizedExperiencesStat, revenueReferenceCopy, type CdpIndustryConfig } from "./shared";

export const qsrCdpConfig: CdpIndustryConfig = {
  icon: CoffeeIcon,
  name: "quick service restaurants",
  titleCaseName: "Quick Service Restaurant",
  abbreviatedName: "QSR",
  pluralNoun: "QSRs",
  color: "yellow",
  seoCopy:
    "Leverage all your quick service restaurant data, including customers, menu items, orders, locations, promotions, and loyalty members.",
  heroImage: {
    src: hero,
    alt: "A photograph of pizza and wings with a list of example data tables overlayed. The data tables included are for loyalty members, locations, menu items, orders, and promotions.",
  },
  adoptingCdpsDescription: (
    <>
      The quick-service restaurant industry is incredibly competitive, and building loyalty takes time.{" "}
      {revenueReferenceCopy}
    </>
  ),
  featuredOutcomes: [
    {
      icon: TrendIcon,
      color: "blue",
      title: "Maximize LTV & AOV",
    },
    {
      icon: MoneyIcon,
      color: "green",
      title: "Drive incremental revenue",
    },
    {
      icon: HeartIcon,
      color: "red",
      title: "Build loyalty",
    },
  ],
  dataStats: [
    {
      title: "$1.1T",
      description: (
        <Balancer>
          estimated market size of
          <Box as="br" display={{ base: "block", lg: "none" }} /> the QSR industry
        </Balancer>
      ),
      href: "https://www.statista.com/statistics/1186194/fast-food-restaurant-industry-market-size-global/#:~:text=Global%20quick%20service%20restaurant%20industry%20market%20size%202022%2D2023&text=In%202023%2C%20the%20market%20size,increase%20over%20the%20previous%20year.",
    },
    personalizedExperiencesStat,
    dataBreachStat,
  ],
  personalizationCards: [
    {
      title:
        "McDonalds analyzes real-time audience data  to deliver personalized offers and optimize pricing strategies for all mobile app users",
      href: "https://tepia.co/plexure-and-mcdonalds-revolutionizing-personalized-experiences/",
    },
    {
      title:
        "Starbucks delivers personalized promotions and gamified rewards to 34 million mobile users to drive 31% of all transactions",
      href: "https://www.geekwire.com/2024/starbucks-mobile-orders-surpass-30-of-total-transactions-at-u-s-stores-for-the-first-time/",
    },
    {
      title:
        "Chipotle uses data to drive personalized recommendations for upsells to increase average order value (AOV)",
      href: "https://www.pymnts.com/earnings/2024/chipotle-taps-targeted-upselling-as-restaurants-compete-to-personalize-the-experience/",
    },
  ],
  useCases: {
    performanceMarketing: [
      // First row
      {
        icon: PersonWithCheckIcon,
        title: "Acquisition campaigns",
        description:
          "Drive new customer visits by using demographic and location data to target nearby users or lookalike audiences with first-time visit promotions, increasing foot traffic.",
      },
      {
        icon: MinusCircleIcon,
        title: "Suppression campaigns",
        description:
          "Reduce wasted ad spend by suppressing frequent customers from acquisition campaigns using CRM data, reallocating budget toward untapped or lapsed customers.",
      },
      {
        icon: CalendarIcon,
        title: "Seasonal campaigns",
        description:
          "Boost sales during limited-time promotions by highlighting seasonal menu items or exclusive discounts, increasing urgency and order volume.",
      },
      // Second row
      {
        icon: MoneyIcon,
        title: "Cross-sell campaigns",
        description:
          "Increase order value by promoting add-ons using past order data to serve targeted ads, boosting AOV.",
      },
      {
        icon: SettingsIcon,
        title: "Conversion APIs",
        description:
          "Share online and offline conversion data with ad platforms, enabling algorithms to better identify and target users with a higher likelihood of converting to improve campaign performance.",
      },
      {
        icon: GlobeIcon,
        title: "QSR media networks",
        description:
          "Monetize customer insights by offering anonymized audience segments to beverage and snack brand partners for targeted advertising, generating incremental revenue.",
      },
    ],
    lifecycleMarketing: [
      // First row
      {
        icon: MoneyIcon,
        title: "Upsell & cross-sell campaigns",
        description:
          "Recommend meal bundles, add-ons, or premium menu items based on previous orders to increase ticket size and average check value.",
      },
      {
        icon: RefreshIcon,
        title: "Reactivation campaigns",
        description:
          "Offer exclusive discounts or free delivery to bring back lapsed customers and drive repeat orders.",
      },
      {
        icon: GiftIcon,
        title: "Birthday campaigns",
        description:
          "Send personalized birthday offers, such as free desserts or discounts, to build loyalty and encourage celebratory visits.",
      },
      // Second row
      {
        icon: HeartIcon,
        title: "Loyalty program engagement",
        description:
          "Reward customers with double points, exclusive menu items, or surprise perks to boost program participation and customer retention.",
      },
      {
        icon: CalendarIcon,
        title: "Seasonal campaigns",
        description:
          "Promote limited-time menu items, holiday-themed meals, or seasonal discounts to drive demand during peak dining periods.",
      },
      {
        icon: PinIcon,
        title: "Localized promotions",
        description:
          "Highlight region-specific menu items, in-store deals, or exclusive restaurant promotions to increase local foot traffic and engagement.",
      },
      // Third row
      {
        icon: PersonWithCheckIcon,
        title: "Win-back campaigns",
        description:
          "Re-engage customers who had a negative experience by offering discounts, free items, or personalized apologies to recover lost business.",
      },
      {
        icon: PeopleIcon,
        title: "Family bundle campaigns",
        description:
          "Promote group-friendly meal bundles designed for families or large gatherings to drive higher ticket sizes and increase revenue.",
      },
      {
        icon: MoneyIcon,
        title: "High-spender rewards",
        description:
          "Recognize and reward high-value customers with exclusive perks, early access to new menu items, or VIP promotions to increase visit frequency.",
      },
    ],
    onsitePersonalization: [
      // First row
      {
        icon: LightningIcon,
        title: "Dynamic home page content",
        description:
          "Display recent orders, loyalty rewards, or special deals tailored to the user to increase app engagement and conversions.",
      },
      {
        icon: CoffeeIcon,
        title: "Dynamic menu recommendations",
        description:
          "Use previous orders to recommend popular menu items or frequently paired items to boost order value and customer satisfaction.",
      },
      {
        icon: CornerDownRightIcon,
        title: "Next best action",
        description:
          "Encourage actions like joining the loyalty program, saving a favorite order, or leaving a review to drive engagement and future purchases.",
      },
      // Second row
      {
        icon: ClockIcon,
        title: "Order progress tracking",
        description:
          "Show real-time order status, delivery tracking, or pickup readiness notifications to improve customer satisfaction and reduce service anxiety.",
      },
      {
        icon: CalendarIcon,
        title: "Special day reminders",
        description:
          "Notify users about upcoming events like National Pizza Day or local holidays to drive engagement and timely orders.",
      },
      {
        icon: SparklesIcon,
        title: "Gamified loyalty rewards",
        description:
          "Offer badges, progress tracking, or tiers for loyalty program milestones to increase retention and repeat visits.",
      },
      // Third row
      {
        icon: PersonWithCheckIcon,
        title: "Customized meal deals",
        description:
          "Highlight build-your-own meal options tailored to customer preferences to improve engagement and satisfaction.",
      },
      {
        icon: AlertIcon,
        title: "Nearby deals notifications",
        description:
          "Use geolocation data to notify users of local deals and exclusive promotions to drive foot traffic and real-time engagement.",
      },
      {
        icon: MoneyIcon,
        title: "Upsell & cross-sell suggestions",
        description:
          "Recommend high-value add-ons, premium upgrades, or limited-time offers based on past purchases and browsing behavior to increase average order value and customer satisfaction.",
      },
    ],
  },
  considerations: {
    dataModeling: {
      traditional:
        "Uses predefined models that may not fully capture dine-in versus takeout preferences or meal customization",
      composable:
        "Supports tailored models to handle order history, dining preferences, and loyalty program engagement",
    },
    audienceManagement: {
      traditional:
        "Supports broad segmentation but struggles with identifying frequent customers or menu item preferences",
      composable:
        "Enables granular segmentation using order frequency, location preferences, and menu customization data",
    },
    customerJourneys: {
      traditional: "Provides standard templates for campaigns like loyalty rewards or email promotions",
      composable:
        "Powers fully adaptable journeys for events like new menu launches, seasonal promotions, and loyalty tier upgrades",
    },
    identityResolution: {
      traditional:
        "Relies on out-of-the-box algorithms that may not unify profiles across mobile apps in-store systems and third-party delivery platforms",
      composable: "Supports custom algorithms to unify profiles from apps, POS systems, and delivery platforms",
    },
  },
  whyHightouchTitleMaxW: "760px",
  audienceOverlap: {
    title: "Frequent high-spend customers who haven’t ordered lately",
    firstCategoryLabel: "VIP app users with AOV > $30 who stopped ordering",
    secondCategoryLabel: "High-spending loyalty members with no recent visits",
  },
};
